// src/components/ValmikiFooter.jsx
// Shared VALMIKI AI LAB footer, with an animated network background
// (constellation mesh, dash streaks, cursor grab lines).
// Self-contained: carries its own CSS in the `css` string below (injected via <style>).
// No external CSS file and no dependency on the page it is placed in.

import React, { useEffect, useRef } from "react";
import { VALMIKI_ROUTES, VALMIKI_NAV_LINKS, ValmikiBrandMark } from "./ValmikiNavbar";

/* ───────────────────────── STYLES (in-file) ───────────────────────── */

const css = `@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500&family=IBM+Plex+Sans:wght@400;500;600&family=Newsreader:ital,opsz,wght@0,6..72,400;0,6..72,500;0,6..72,600;1,6..72,400&display=swap');
.vl-footer{
  --ink:#0B1626; --ink-2:#132238; --ink-3:#08111E;
  --paper:#F3F5F8; --white:#FFFFFF;
  --gold:#C8A24A; --gold-hi:#E0BC62; --gold-deep:#7D5F12;
  --slate:#4F5C73; --on-ink:#F3F5F8; --on-ink-mute:rgba(243,245,248,.72);
  --line:rgba(11,22,38,.14); --line-ink:rgba(243,245,248,.16);
  --serif:'Newsreader',Georgia,'Times New Roman',serif;
  --sans:'IBM Plex Sans','Segoe UI',Arial,sans-serif;
  font-family:var(--sans); line-height:1.6; -webkit-font-smoothing:antialiased;
}
.vl-footer, .vl-footer *{box-sizing:border-box;}
:where(.vl-footer) :where(p,ul,ol){margin:0;padding:0;}
:where(.vl-footer) :where(ul,ol){list-style:none;}
.vl-footer :focus-visible{outline:2px solid var(--gold-hi);outline-offset:3px;}
.vl-wrap{max-width:1120px;margin:0 auto;padding:0 24px;}
@media (max-width:560px){.vl-wrap{padding:0 20px;}}
.vl-brand{display:flex;align-items:center;gap:12px;text-decoration:none;color:var(--on-ink);
  font:600 17px/1 var(--sans);letter-spacing:.14em;}
/* footer */
.vl-footer{position:relative;overflow:hidden;isolation:isolate;background:var(--ink-3);color:var(--on-ink);padding:56px 0 40px;}
.vl-footer-bg{position:absolute;inset:0;width:100%;height:100%;display:block;z-index:0;pointer-events:none;}
.vl-footer .vl-wrap{position:relative;z-index:1;}
.vl-footer-row{display:flex;flex-wrap:wrap;justify-content:space-between;gap:32px;padding-bottom:36px;border-bottom:1px solid var(--line-ink);}
.vl-footer p{font-size:14px;color:var(--on-ink-mute);max-width:38ch;margin-top:14px;}
.vl-footer nav{display:flex;flex-wrap:wrap;gap:12px 28px;align-items:flex-start;}
.vl-footer nav a{font-size:13px;letter-spacing:.08em;text-decoration:none;color:var(--on-ink-mute);}
.vl-footer nav a:hover{color:var(--on-ink);}
.vl-footer .vl-copy{padding-top:24px;margin-top:0;max-width:none;font-size:13px;color:var(--on-ink-mute);}

`;

/* ───────────────────────── ANIMATED BACKGROUND ─────────────────────────
   Constellation / polygon-mesh background, matching the reference:
   - sparse drifting nodes joined by thin long lines (forms triangles/polygons)
   - a few accent-coloured nodes
   - short drifting dash streaks
   - cursor "grab": lines reach from the pointer to nearby nodes
   Pauses off-screen / hidden tab. Reduced-motion users get one static frame. */

const ACCENT = "226,59,59";      // accent nodes (RGB). Gold alternative: "224,188,98"
const LINK_DIST = 230;           // max distance for a line between two nodes
const GRAB_DIST = 190;           // pointer → node line distance
const AREA_PER_NODE = 24000;     // lower = more nodes
const SPEED = 0.32;              // node drift speed (px/frame)

function NetworkBackground() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const host = canvas.parentElement;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let w = 0, h = 0, dpr = 1;
    let nodes = [], dashes = [];
    let raf = 0, running = false, visible = true;
    const mouse = { x: -9999, y: -9999, on: false };

    const rand = (a, b) => a + Math.random() * (b - a);

    const build = () => {
      const count = Math.min(110, Math.max(22, Math.round((w * h) / AREA_PER_NODE)));
      nodes = Array.from({ length: count }, () => {
        const ang = rand(0, Math.PI * 2);
        const sp = rand(0.4, 1) * SPEED;
        return {
          x: rand(0, w), y: rand(0, h),
          vx: Math.cos(ang) * sp, vy: Math.sin(ang) * sp,
          r: rand(1, 2.2),
          accent: Math.random() < 0.13,
        };
      });
      dashes = Array.from({ length: Math.max(4, Math.round(count / 8)) }, () => ({
        x: rand(0, w), y: rand(0, h),
        len: rand(16, 42), ang: rand(0, Math.PI),
        vx: rand(-0.25, 0.25), vy: rand(-0.25, 0.25),
      }));
    };

    const wrap = (o, pad) => {
      if (o.x < -pad) o.x = w + pad; else if (o.x > w + pad) o.x = -pad;
      if (o.y < -pad) o.y = h + pad; else if (o.y > h + pad) o.y = -pad;
    };

    const step = () => {
      for (const n of nodes) { n.x += n.vx; n.y += n.vy; wrap(n, 12); }
      for (const d of dashes) { d.x += d.vx; d.y += d.vy; wrap(d, 50); }
    };

    const draw = () => {
      ctx.clearRect(0, 0, w, h);

      // dash streaks
      ctx.lineWidth = 1;
      ctx.strokeStyle = "rgba(243,245,248,0.22)";
      for (const d of dashes) {
        const dx = Math.cos(d.ang) * d.len * 0.5;
        const dy = Math.sin(d.ang) * d.len * 0.5;
        ctx.beginPath();
        ctx.moveTo(d.x - dx, d.y - dy);
        ctx.lineTo(d.x + dx, d.y + dy);
        ctx.stroke();
      }

      // node ↔ node lines
      const L2 = LINK_DIST * LINK_DIST;
      for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i];
        for (let j = i + 1; j < nodes.length; j++) {
          const b = nodes[j];
          const dx = a.x - b.x, dy = a.y - b.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < L2) {
            const k = 1 - Math.sqrt(d2) / LINK_DIST;
            ctx.strokeStyle =
              a.accent || b.accent
                ? `rgba(${ACCENT},${(k * 0.38).toFixed(3)})`
                : `rgba(243,245,248,${(k * 0.28).toFixed(3)})`;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }

      // pointer grab lines
      if (mouse.on) {
        const G2 = GRAB_DIST * GRAB_DIST;
        for (const n of nodes) {
          const dx = n.x - mouse.x, dy = n.y - mouse.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < G2) {
            const k = 1 - Math.sqrt(d2) / GRAB_DIST;
            ctx.strokeStyle = `rgba(${ACCENT},${(k * 0.7).toFixed(3)})`;
            ctx.beginPath();
            ctx.moveTo(n.x, n.y);
            ctx.lineTo(mouse.x, mouse.y);
            ctx.stroke();
          }
        }
      }

      // nodes
      for (const n of nodes) {
        ctx.fillStyle = n.accent ? `rgba(${ACCENT},0.95)` : "rgba(243,245,248,0.6)";
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.accent ? n.r + 0.5 : n.r, 0, Math.PI * 2);
        ctx.fill();
      }
    };

    const loop = () => { step(); draw(); raf = requestAnimationFrame(loop); };
    const start = () => {
      if (running || reduceMotion || !visible || document.hidden) return;
      running = true;
      raf = requestAnimationFrame(loop);
    };
    const stop = () => { running = false; cancelAnimationFrame(raf); };

    const resize = () => {
      const r = host.getBoundingClientRect();
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = Math.max(1, r.width);
      h = Math.max(1, r.height);
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      build();
      draw();
    };

    const onMove = (e) => {
      const r = host.getBoundingClientRect();
      mouse.x = e.clientX - r.left;
      mouse.y = e.clientY - r.top;
      mouse.on = true;
    };
    const onLeave = () => { mouse.on = false; };

    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(host);
    const io = new IntersectionObserver(([e]) => {
      visible = e.isIntersecting;
      visible ? start() : stop();
    });
    io.observe(host);
    const onVis = () => (document.hidden ? stop() : start());
    document.addEventListener("visibilitychange", onVis);
    if (!reduceMotion) {
      host.addEventListener("pointermove", onMove);
      host.addEventListener("pointerleave", onLeave);
    }
    start();

    return () => {
      stop();
      ro.disconnect();
      io.disconnect();
      document.removeEventListener("visibilitychange", onVis);
      host.removeEventListener("pointermove", onMove);
      host.removeEventListener("pointerleave", onLeave);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="vl-footer-bg"
      aria-hidden="true"
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        display: "block",
        zIndex: 0,
        pointerEvents: "none",
      }}
    />
  );
}

/* ───────────────────────── COMPONENT ───────────────────────── */

export default function ValmikiFooter() {
  return (
    <footer className="vl-footer">
      <style>{css}</style>
      <NetworkBackground />
      <div className="vl-wrap">
        <div className="vl-footer-row">
          <div>
            <a className="vl-brand" href={VALMIKI_ROUTES.home}>
              <ValmikiBrandMark />VALMIKI AI LAB
            </a>
            <p>Applied AI learning, innovation and career development.</p>
          </div>
          <nav aria-label="Footer">
            {VALMIKI_NAV_LINKS.map((l) => (
              <a key={l.label} href={l.href}>{l.label}</a>
            ))}
            <a href={VALMIKI_ROUTES.partner}>PARTNER WITH US</a>
          </nav>
        </div>
        <p className="vl-copy">
          © {new Date().getFullYear()} VALMIKI AI LAB. Career outcomes and opportunities vary by employer,
          market demand, student performance, skills, portfolio and location.
        </p>
      </div>
    </footer>
  );
}